import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { mergeReducedMotionPreset, resolveExpandTransition, useThemeMotion } from '../animation/themeMotion'
import { useTheme } from '../theme/ThemeContext'
import { chromeFieldBaseClass } from './ChromeSurface'

export type SelectOption = {
  value: string
  label: string
  description?: string
}

type Props = {
  id?: string
  value: string
  onChange: (value: string) => void
  options: SelectOption[]
  ariaLabel: string
  /** 像素 / 极客：直角与粗描边；不传则从当前主题推断，避免漏传仍显示胶囊形 */
  angular?: boolean
  /** compact：与表单输入框同高，用于弹窗内字段 */
  density?: 'default' | 'compact'
  disabled?: boolean
}

/** 下拉层：固定定位 + maxHeight，避免贴底或贴右时溢出视口 */
type MenuLayout = {
  top: number
  left: number
  width: number
  maxHeight: number
}

const VIEWPORT_MARGIN = 8
const GAP = 6
const PREFERRED_MAX_H = 320
const PREFERRED_MAX_H_COMPACT = 260
const OPTION_ROW_HEIGHT = { default: 56, compact: 40 } as const

export function SelectMenu({
  id: extId,
  value,
  onChange,
  options,
  ariaLabel,
  angular: angularProp,
  density = 'default',
  disabled = false,
}: Props) {
  const { theme } = useTheme()
  const angularFromTheme = theme === 'pixel' || theme === 'geek'
  const angular = angularProp !== undefined ? angularProp : angularFromTheme
  const autoId = useId()
  const listId = `${autoId}-list`
  const btnId = extId ?? `${autoId}-btn`
  const [open, setOpen] = useState(false)
  const [menuLayout, setMenuLayout] = useState<MenuLayout | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const portalListRef = useRef<HTMLUListElement>(null)
  /** 外部 mousedown 关闭后，同一手势的 click 可能落到 trigger 上导致再次展开 */
  const ignoreTriggerClickRef = useRef(false)
  const motionPreset = useThemeMotion()
  const reducedMotion = useReducedMotion()
  const dropdownTrans = resolveExpandTransition(
    mergeReducedMotionPreset(motionPreset, !!reducedMotion),
    { reducedMotion: !!reducedMotion },
  )

  const selected = options.find((o) => o.value === value) ?? options[0]
  const compact = density === 'compact'
  const roundChevron = compact
    ? ''
    : angular
      ? 'rounded-none'
      : 'rounded-full'
  const roundOption = angular ? 'rounded-none' : compact ? 'rounded-[var(--radius-sm)]' : 'rounded-xl'
  const roundTrigger = compact
    ? ''
    : angular
      ? 'rounded-none border-2 shadow-none'
      : 'rounded-full shadow-sm'
  const roundList = angular
    ? compact
      ? 'rounded-none border-2 p-1 shadow-none'
      : 'rounded-none border-2 p-1 shadow-none'
    : compact
      ? 'rounded-[var(--radius-sm)] p-1 shadow-float'
      : 'rounded-[var(--radius)] p-1.5 shadow-float'
  const approxRow = OPTION_ROW_HEIGHT[compact ? 'compact' : 'default']
  const preferredMaxH = compact ? PREFERRED_MAX_H_COMPACT : PREFERRED_MAX_H

  const syncPosition = useCallback(() => {
    const el = rootRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    const w = Math.min(r.width, vw - 2 * VIEWPORT_MARGIN)
    const estContentH = Math.min(preferredMaxH, options.length * approxRow + (compact ? 12 : 16))
    const spaceBelow = vh - r.bottom - GAP - VIEWPORT_MARGIN
    const spaceAbove = r.top - GAP - VIEWPORT_MARGIN
    const placeBelow =
      spaceBelow >= Math.min(140, estContentH) || spaceBelow >= spaceAbove * 0.85
    let top: number
    let maxH: number
    if (placeBelow) {
      top = r.bottom + GAP
      maxH = Math.min(preferredMaxH, Math.max(120, spaceBelow))
    } else {
      maxH = Math.min(preferredMaxH, Math.max(120, spaceAbove))
      const menuH = Math.min(maxH, estContentH)
      top = Math.max(VIEWPORT_MARGIN, r.top - GAP - menuH)
    }
    let left = r.left
    if (left + w > vw - VIEWPORT_MARGIN) {
      left = Math.max(VIEWPORT_MARGIN, vw - VIEWPORT_MARGIN - w)
    }
    if (left < VIEWPORT_MARGIN) {
      left = VIEWPORT_MARGIN
    }
    setMenuLayout({ top, left, width: w, maxHeight: maxH })
  }, [approxRow, compact, options.length, preferredMaxH])

  const refinePositionWithMeasuredHeight = useCallback(() => {
    const trigger = rootRef.current
    const list = portalListRef.current
    if (!trigger || !list || !menuLayout) return
    const r = trigger.getBoundingClientRect()
    const measuredH = list.getBoundingClientRect().height
    if (measuredH <= 0) return
    const spaceAbove = r.top - GAP - VIEWPORT_MARGIN
    const spaceBelow = window.innerHeight - r.bottom - GAP - VIEWPORT_MARGIN
    const estContentH = Math.min(preferredMaxH, options.length * approxRow + (compact ? 12 : 16))
    const placeBelow =
      spaceBelow >= Math.min(140, estContentH) || spaceBelow >= spaceAbove * 0.85
    if (placeBelow) return
    const top = Math.max(VIEWPORT_MARGIN, r.top - GAP - measuredH)
    if (Math.abs(top - menuLayout.top) < 1) return
    setMenuLayout((prev) => (prev ? { ...prev, top } : prev))
  }, [approxRow, compact, menuLayout, options.length, preferredMaxH])

  useLayoutEffect(() => {
    if (!open) {
      setMenuLayout(null)
      return
    }
    syncPosition()
    const onScroll = () => syncPosition()
    const onResize = () => syncPosition()
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    const scrollParents: HTMLElement[] = []
    let parent = rootRef.current?.parentElement ?? null
    while (parent) {
      const { overflowY, overflow } = getComputedStyle(parent)
      if (/(auto|scroll|overlay)/.test(`${overflowY} ${overflow}`)) {
        scrollParents.push(parent)
        parent.addEventListener('scroll', onScroll, { passive: true })
      }
      parent = parent.parentElement
    }
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
      for (const el of scrollParents) {
        el.removeEventListener('scroll', onScroll)
      }
    }
  }, [open, syncPosition])

  useLayoutEffect(() => {
    if (!open || !menuLayout) return
    refinePositionWithMeasuredHeight()
    const list = portalListRef.current
    if (!list || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => refinePositionWithMeasuredHeight())
    observer.observe(list)
    return () => observer.disconnect()
  }, [open, menuLayout, refinePositionWithMeasuredHeight])

  useEffect(() => {
    if (disabled) setOpen(false)
  }, [disabled])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node
      if (rootRef.current?.contains(t)) return
      if (portalListRef.current?.contains(t)) return
      ignoreTriggerClickRef.current = true
      setOpen(false)
      window.setTimeout(() => {
        ignoreTriggerClickRef.current = false
      }, 0)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDoc, true)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('mousedown', onDoc, true)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [open])

  const listContent =
    open && menuLayout ? (
      <motion.ul
        ref={portalListRef}
        id={listId}
        role="listbox"
        aria-labelledby={btnId}
        className={[
          'select-menu-dropdown scroll-region overflow-y-auto overflow-x-hidden border border-line',
          compact ? 'px-1' : 'pe-2 sm:pe-2.5',
          roundList,
        ].join(' ')}
        style={{
          position: 'fixed',
          top: menuLayout.top,
          left: menuLayout.left,
          width: menuLayout.width,
          maxHeight: menuLayout.maxHeight,
          zIndex: 120000,
          backgroundColor: 'var(--menu-solid)',
          boxSizing: 'border-box',
        }}
        initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -10, scale: 0.985 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -6, scale: 0.992 }}
        transition={dropdownTrans}
      >
          {options.map((opt) => {
            const isActive = opt.value === value
            return (
              <li key={opt.value} role="option" aria-selected={isActive}>
                <button
                  type="button"
                  className={[
                    'group flex w-full text-left antialiased transition',
                    compact ? 'flex-row items-center px-2.5 py-2' : 'flex-col px-3 py-2.5',
                    roundOption,
                    isActive
                      ? 'bg-[var(--segment-active-bg)] text-[var(--segment-active-fg)]'
                      : 'text-ink hover:bg-surface-muted hover:text-ink',
                  ].join(' ')}
                  style={{
                    backgroundColor: isActive ? 'var(--segment-active-bg)' : undefined,
                  }}
                  onClick={() => {
                    onChange(opt.value)
                    setOpen(false)
                  }}
                >
                  <span className={compact ? 'truncate text-sm font-medium' : 'text-sm font-semibold'}>
                    {opt.label}
                  </span>
                  {compact && opt.description ? (
                    <span
                      className={[
                        'min-w-0 flex-1 truncate text-xs font-normal',
                        isActive ? 'text-[var(--segment-active-fg)] opacity-80' : 'text-ink-soft',
                      ].join(' ')}
                    >
                      {opt.description}
                    </span>
                  ) : null}
                  {!compact && opt.description ? (
                    <span
                      className={[
                        'mt-0.5 text-xs font-normal',
                        isActive ? 'text-[var(--segment-active-fg)]' : 'text-ink',
                      ].join(' ')}
                    >
                      {opt.description}
                    </span>
                  ) : null}
                </button>
              </li>
            )
          })}
      </motion.ul>
    ) : null

  return (
    <>
      <div ref={rootRef} className="relative z-[1] w-full min-w-0">
        <button
          id={btnId}
          type="button"
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={listId}
          disabled={disabled}
          onClick={() => {
            if (ignoreTriggerClickRef.current) {
              ignoreTriggerClickRef.current = false
              return
            }
            if (!disabled) setOpen((o) => !o)
          }}
          className={[
            'select-menu-trigger flex w-full min-w-0 items-center text-left antialiased outline-none transition hover:text-ink focus-visible:ring-2 focus-visible:ring-accent',
            compact
              ? [chromeFieldBaseClass(angular), 'gap-2 overflow-hidden px-2.5 py-2'].join(' ')
              : ['gap-3 border border-line px-4 py-3 hover:border-accent', roundTrigger].join(' '),
            disabled ? 'pointer-events-none cursor-not-allowed opacity-45 saturate-75' : '',
          ].join(' ')}
          style={compact ? undefined : { backgroundColor: 'var(--menu-solid)' }}
        >
          <span className="min-w-0 flex-1">
            <AnimatePresence initial={false} mode="wait">
              <motion.span
                key={`${selected?.value ?? 'none'}-label`}
                className={[
                  'block truncate text-ink',
                  compact ? 'text-sm font-medium' : 'text-sm font-semibold',
                ].join(' ')}
                initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -4 }}
                transition={{ duration: reducedMotion ? 0.14 : 0.2, ease: [0.22, 1, 0.36, 1] }}
              >
                {selected?.label}
              </motion.span>
            </AnimatePresence>
            <AnimatePresence initial={false} mode="wait">
              {!compact && selected?.description ? (
                <motion.span
                  key={`${selected.value}-desc`}
                  className="mt-0.5 block truncate text-xs font-normal text-ink"
                  initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 3 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -3 }}
                  transition={{ duration: reducedMotion ? 0.14 : 0.2, ease: [0.22, 1, 0.36, 1] }}
                >
                  {selected.description}
                </motion.span>
              ) : null}
            </AnimatePresence>
          </span>
          <span
            className={[
              'inline-flex shrink-0 items-center justify-center text-ink-soft transition',
              compact ? '-mr-0.5' : 'h-8 w-8 border border-line',
              roundChevron,
              open ? 'rotate-180' : '',
            ].join(' ')}
            style={compact ? undefined : { backgroundColor: 'var(--menu-muted)' }}
            aria-hidden
          >
            <svg
              width={compact ? 16 : 16}
              height={compact ? 16 : 16}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </span>
        </button>
      </div>
      {typeof document !== 'undefined' && listContent ? createPortal(listContent, document.body) : null}
    </>
  )
}
